import { FormEvent, useState } from "react"
import { Head, router, useForm, usePage } from "@inertiajs/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { AccountShell } from "@/components/AccountShell"
import { PageHeader } from "@/components/PageHeader"
import { StripeProvider } from "@/components/StripeProvider"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { SubNav } from "@/components/ui/sub-nav"
import { fetchJson, formatCents } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

interface SubscriptionInfo {
  id: number
  status: string
  quantity: number
  pending_quantity: number | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  annual_total_cents: number
}

type BillingProps = PageProps<{
  subscription: SubscriptionInfo | null
  lifetime: { deal_name: string; redeemed_at: string | null }[]
  payment_method: { brand: string; last4: string; exp_month: number; exp_year: number } | null
  invoice_info: {
    billing_name: string | null
    billing_address: string | null
    billing_extra: string | null
  }
  seat_summary: { total: number; assigned: number }
  max_seats: number
  stripe_publishable_key: string
}>

const STATUS_TONE: Record<string, "accent" | "signal" | "muted" | "solid"> = {
  active: "accent",
  past_due: "signal",
  canceled: "muted",
  unpaid: "muted",
  incomplete: "muted",
}

export default function BillingShow() {
  const { props } = usePage<BillingProps>()
  const { subscription, lifetime, payment_method: paymentMethod } = props
  const [tab, setTab] = useState("plan")

  const tabs = [
    { key: "plan", label: "Plan" },
    ...(subscription ? [{ key: "payment", label: "Payment method" }] : []),
    { key: "invoice", label: "Invoice details" },
  ]

  return (
    <>
      <Head title="Billing">
        <meta
          name="description"
          content="Manage your Flywrite plan — seats, payment method, and auto-renewal."
        />
        <meta property="og:title" content="Billing" />
        <meta
          property="og:description"
          content="Manage your Flywrite plan — seats, payment method, and auto-renewal."
        />
      </Head>
      <AccountShell>
        <PageHeader
          title="Billing"
          description="Your plan, payment method, and invoice details."
          tabs={<SubNav items={tabs} active={tab} onChange={setTab} ariaLabel="Billing sections" />}
        />

        {tab === "plan" && (
          <div className="mt-8 space-y-6">
            {/* Lifetime licenses */}
            {lifetime.map((redemption) => (
              <div key={redemption.deal_name} className="callout callout-accent">
                <div className="flex items-center justify-between">
                  <div>
                    <h2>{redemption.deal_name}</h2>
                    <p className="mt-1 text-sm text-ink-body">
                      Lifetime license — 1 seat, never expires, updates forever.
                      {redemption.redeemed_at && ` Redeemed ${redemption.redeemed_at}.`}
                    </p>
                  </div>
                  <Badge tone="accent">Active</Badge>
                </div>
              </div>
            ))}

            {/* Subscription plan */}
            {subscription ? (
              <div className="callout">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2>Annual plan</h2>
                    <p className="mt-1 text-sm text-ink-body">
                      <strong>{subscription.quantity}</strong>{" "}
                      {subscription.quantity === 1 ? "seat" : "seats"} ·{" "}
                      {formatCents(subscription.annual_total_cents)}/year
                    </p>
                    {subscription.current_period_end && (
                      <p className="mt-1 text-sm text-ink-muted">
                        {subscription.cancel_at_period_end ? "Active until" : "Renews"}{" "}
                        {subscription.current_period_end}
                        {subscription.pending_quantity &&
                          ` · renewing with ${subscription.pending_quantity} ${
                            subscription.pending_quantity === 1 ? "seat" : "seats"
                          }`}
                      </p>
                    )}
                  </div>
                  <Badge tone={STATUS_TONE[subscription.status] ?? "muted"}>
                    {subscription.cancel_at_period_end ? "Canceling" : subscription.status}
                  </Badge>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <AddSeatsDialog subscription={subscription} maxSeats={props.max_seats} />
                  <DecreaseSeatsDialog
                    subscription={subscription}
                    assigned={props.seat_summary.assigned}
                  />
                  {subscription.pending_quantity && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => router.post("/account/billing/clear_decrease", {}, { preserveScroll: true })}
                    >
                      Keep {subscription.quantity} seats
                    </Button>
                  )}
                  {subscription.cancel_at_period_end ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => router.post("/account/billing/resume", {}, { preserveScroll: true })}
                    >
                      Resume auto-renewal
                    </Button>
                  ) : (
                    <CancelDialog />
                  )}
                </div>
              </div>
            ) : lifetime.length === 0 ? (
              <div className="callout">
                <h2>No active plan</h2>
                <p className="mt-1 text-sm text-ink-muted">
                  <a href="/buy">See pricing</a> to get Flywrite for your team.
                </p>
              </div>
            ) : null}
          </div>
        )}

        {tab === "payment" && subscription && (
          <div className="callout mt-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-body">
                {paymentMethod
                  ? `${paymentMethod.brand.toUpperCase()} •••• ${paymentMethod.last4} — expires ${paymentMethod.exp_month}/${paymentMethod.exp_year}`
                  : "No card on file yet."}
              </p>
              <UpdateCardDialog publishableKey={props.stripe_publishable_key} />
            </div>
          </div>
        )}

        {tab === "invoice" && (
          <div className="mt-8">
            <InvoiceInfoForm invoiceInfo={props.invoice_info} />
          </div>
        )}
      </AccountShell>
    </>
  )
}

function AddSeatsDialog({
  subscription,
  maxSeats,
}: {
  subscription: SubscriptionInfo
  maxSeats: number
}) {
  const [open, setOpen] = useState(false)
  const [additional, setAdditional] = useState(1)
  const [preview, setPreview] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const remaining = maxSeats - subscription.quantity

  async function loadPreview(count: number) {
    setPreviewing(true)
    setError(null)
    setPreview(null)
    const result = await fetchJson<{ amount_due_cents?: number; errors?: Record<string, string> }>(
      "/account/billing/add_seats/preview",
      { body: { additional: count } },
    )
    setPreviewing(false)
    if (result.ok && typeof result.data.amount_due_cents === "number") {
      setPreview(result.data.amount_due_cents)
    } else {
      setError(result.data.errors?.base ?? "Couldn't calculate the prorated amount.")
    }
  }

  function confirm() {
    setSubmitting(true)
    router.post(
      "/account/billing/add_seats",
      { additional },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          setOpen(false)
        },
      },
    )
  }

  if (remaining <= 0) return null

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) void loadPreview(additional)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          Add seats
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Add seats</DialogTitle>
          <DialogDescription>
            You'll be charged a prorated amount today covering the rest of your current billing
            period; renewals include the new seats at full price.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="additional-seats">Additional seats</label>
            <Input
              id="additional-seats"
              type="number"
              min={1}
              max={remaining}
              value={additional}
              onChange={(e) => {
                const value = Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), remaining)
                setAdditional(value)
                void loadPreview(value)
              }}
            />
          </div>
          <p className="text-sm text-ink-body">
            {previewing
              ? "Calculating prorated charge…"
              : preview !== null
                ? `Due today: ${formatCents(preview)}`
                : error}
          </p>
        </div>
        <DialogFooter>
          <Button onClick={confirm} disabled={submitting || previewing || preview === null}>
            {submitting ? "Charging…" : `Add ${additional} ${additional === 1 ? "seat" : "seats"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DecreaseSeatsDialog({
  subscription,
  assigned,
}: {
  subscription: SubscriptionInfo
  assigned: number
}) {
  const [open, setOpen] = useState(false)
  const minimum = Math.max(assigned, 1)
  const [quantity, setQuantity] = useState(Math.max(subscription.quantity - 1, minimum))

  if (subscription.quantity <= 1 || subscription.cancel_at_period_end) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Reduce seats
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reduce seats at renewal</DialogTitle>
          <DialogDescription>
            Seat decreases take effect at your next renewal — no mid-cycle credits. You currently
            have {assigned} assigned {assigned === 1 ? "seat" : "seats"}, which is the minimum.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor="new-quantity">Seats at renewal</label>
          <Input
            id="new-quantity"
            type="number"
            min={minimum}
            max={subscription.quantity - 1}
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || minimum)}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() =>
              router.post(
                "/account/billing/schedule_decrease",
                { quantity },
                { preserveScroll: true, onSuccess: () => setOpen(false) },
              )
            }
          >
            Schedule reduction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelDialog() {
  const [open, setOpen] = useState(false)
  const form = useForm({ reason: "" })
  const { props } = usePage<PageProps>()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/account/billing/cancel", {
      preserveScroll: true,
      onSuccess: () => setOpen(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Cancel auto-renewal
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Cancel auto-renewal?</DialogTitle>
          <DialogDescription>
            Your plan stays fully active until the end of the current period. After that, Flywrite
            switches to read-only mode — your files remain yours, always viewable and exportable.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="cancel-reason">Why are you canceling?</label>
            <textarea
              id="cancel-reason"
              className="form-control form-control-textarea"
              rows={3}
              required
              value={form.data.reason}
              aria-invalid={!!props.errors?.reason}
              onChange={(e) => form.setData("reason", e.target.value)}
            />
            {props.errors?.reason && (
              <p className="text-xs text-danger-display">{props.errors.reason}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Keep my plan
            </Button>
            <Button type="submit" variant="danger" disabled={form.processing}>
              Cancel auto-renewal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function UpdateCardDialog({ publishableKey }: { publishableKey: string }) {
  const [open, setOpen] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function begin(next: boolean) {
    setOpen(next)
    if (!next || clientSecret) return
    const result = await fetchJson<{ client_secret?: string; errors?: Record<string, string> }>(
      "/account/billing/setup_intent",
    )
    if (result.ok && result.data.client_secret) {
      setClientSecret(result.data.client_secret)
    } else {
      setError(result.data.errors?.base ?? "Couldn't start the card update.")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => void begin(next)}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          Update card
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update payment method</DialogTitle>
          <DialogDescription>
            Your new card becomes the default for renewals and seat additions.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-danger-display">{error}</p>}
        {clientSecret ? (
          <StripeProvider publishableKey={publishableKey} clientSecret={clientSecret}>
            <SetupForm />
          </StripeProvider>
        ) : (
          !error && <p className="text-sm text-ink-muted">Loading…</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SetupForm() {
  const stripe = useStripe()
  const elements = useElements()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSaving(true)
    setError(null)
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/account/billing` },
    })
    setError(result.error?.message ?? "Couldn't save the card. Please try again.")
    setSaving(false)
  }

  return (
    <form onSubmit={save} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-danger-display">{error}</p>}
      <DialogFooter>
        <Button type="submit" disabled={!stripe || saving}>
          {saving ? "Saving…" : "Save card"}
        </Button>
      </DialogFooter>
    </form>
  )
}

function InvoiceInfoForm({
  invoiceInfo,
}: {
  invoiceInfo: { billing_name: string | null; billing_address: string | null; billing_extra: string | null }
}) {
  const form = useForm({
    billing_name: invoiceInfo.billing_name ?? "",
    billing_address: invoiceInfo.billing_address ?? "",
    billing_extra: invoiceInfo.billing_extra ?? "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch("/account/billing/invoice_info", { preserveScroll: true })
  }

  return (
    <div className="callout">
      <p className="text-sm text-ink-muted">
        Shown on your downloadable PDF invoices — company name, address, VAT/tax ID, or anything
        else you need.
      </p>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="space-y-2">
          <label htmlFor="billing_name">Company / bill-to name</label>
          <Input
            id="billing_name"
            value={form.data.billing_name}
            onChange={(e) => form.setData("billing_name", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="billing_address">Address</label>
          <textarea
            id="billing_address"
            className="form-control form-control-textarea"
            rows={3}
            value={form.data.billing_address}
            onChange={(e) => form.setData("billing_address", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="billing_extra">VAT / tax ID / notes</label>
          <textarea
            id="billing_extra"
            className="form-control form-control-textarea"
            rows={2}
            value={form.data.billing_extra}
            onChange={(e) => form.setData("billing_extra", e.target.value)}
          />
        </div>
        <Button type="submit" variant="secondary" disabled={form.processing}>
          Save invoice details
        </Button>
      </form>
    </div>
  )
}
